import type { UserInterfaceConfigurationType } from "@validereinc/domain";
import { UserInterfaceConfigurationVisibility } from "@validereinc/domain";

export const pickDefaultOrFirstUIConfiguration = (
  configurations: UserInterfaceConfigurationType[]
): UserInterfaceConfigurationType | undefined =>
  configurations.find((configuration) => configuration?.is_default) ??
  configurations[0];

export const partitionUIConfigurationsByVisibility = (
  configurations: UserInterfaceConfigurationType[],
  userId: string
): {
  personal: UserInterfaceConfigurationType[];
  company: UserInterfaceConfigurationType[];
} => ({
  personal: configurations.filter(
    (configuration) =>
      configuration.visibility ===
        UserInterfaceConfigurationVisibility.PERSONAL &&
      configuration.owner_id === userId
  ),
  company: configurations.filter(
    (configuration) =>
      configuration.visibility === UserInterfaceConfigurationVisibility.COMPANY
  ),
});

export const doesUIConfigurationKeyMatchStorageKey = (
  configurationKey: string[],
  storageKeyParts: string[]
): boolean =>
  configurationKey.length === storageKeyParts.length &&
  configurationKey.every((part, index) => part === storageKeyParts[index]);

/**
 * Resolve the effective configuration for a table key: personal overrides
 * company when both exist.
 */
export const resolveUIConfigurationForKey = (
  configurations: UserInterfaceConfigurationType[],
  storageKeyParts: string[],
  userId: string
): UserInterfaceConfigurationType | undefined => {
  const matchingConfigurations = configurations.filter((configuration) =>
    doesUIConfigurationKeyMatchStorageKey(configuration.key, storageKeyParts)
  );

  if (!matchingConfigurations.length) {
    return undefined;
  }

  const { personal, company } = partitionUIConfigurationsByVisibility(
    matchingConfigurations,
    userId
  );

  return (
    pickDefaultOrFirstUIConfiguration(personal) ??
    pickDefaultOrFirstUIConfiguration(company)
  );
};

export const resolveUITableConfigurationsByKey = (
  configurations: UserInterfaceConfigurationType[],
  userId: string
): Map<string, UserInterfaceConfigurationType> => {
  const configurationsByKey = configurations.reduce<
    Map<string, UserInterfaceConfigurationType[]>
  >((grouped, configuration) => {
    const serializedKey = JSON.stringify(configuration.key);
    const existing = grouped.get(serializedKey) ?? [];

    grouped.set(serializedKey, [...existing, configuration]);
    return grouped;
  }, new Map());

  return [...configurationsByKey.entries()].reduce<
    Map<string, UserInterfaceConfigurationType>
  >((resolved, [serializedKey, configurationsForKey]) => {
    const key = JSON.parse(serializedKey) as string[];
    const winner = resolveUIConfigurationForKey(
      configurationsForKey,
      key,
      userId
    );

    if (winner) {
      resolved.set(serializedKey, winner);
    }

    return resolved;
  }, new Map());
};

export const resolveCompanyUIConfigurationForKey = (
  configurations: UserInterfaceConfigurationType[],
  storageKeyParts: string[]
): UserInterfaceConfigurationType | undefined => {
  const companyConfigurations = configurations.filter(
    (configuration) =>
      configuration.visibility ===
        UserInterfaceConfigurationVisibility.COMPANY &&
      doesUIConfigurationKeyMatchStorageKey(configuration.key, storageKeyParts)
  );

  return pickDefaultOrFirstUIConfiguration(companyConfigurations);
};

export const resolvePersonalUIConfigurationForKey = (
  configurations: UserInterfaceConfigurationType[],
  storageKeyParts: string[],
  userId: string
): UserInterfaceConfigurationType | undefined => {
  const personalConfigurations = configurations.filter(
    (configuration) =>
      configuration.visibility ===
        UserInterfaceConfigurationVisibility.PERSONAL &&
      configuration.owner_id === userId &&
      doesUIConfigurationKeyMatchStorageKey(configuration.key, storageKeyParts)
  );

  return pickDefaultOrFirstUIConfiguration(personalConfigurations);
};
